/**
 * ZPL II parser: turns printer code into a Label Document Model.
 *
 * Ported from ns-web resources/js/components/common/label-viewer/utils/zpl-parser.js
 * Changes from NS:
 *  - The field is a state machine: ^FO/^FT set the origin, modifiers accumulate,
 *    ^FS emits. NS walked a nested loop, so a ^BY between ^FO and ^BC (as in
 *    migrations 019/022) was taken for the barcode and its module width lost.
 *  - ^FD data ends at the next ^ or ~ (Zebra's rule), not only at ^FS, and is
 *    not trimmed. CR/LF are dropped everywhere, as the printer does.
 *  - ^FH is a flag with an optional indicator character that decodes the
 *    following ^FD; NS swallowed the ^FD into the ^FH data. Hex escapes are
 *    decoded as UTF-8 under ^CI28.
 *  - The QR error-correction level comes from the ^FD prefix ("QA," = Q),
 *    falling back to the ^BQ parameter. NS always kept the ^BQ value.
 *  - ^FW is persistent (it's a default for later fields), and ^BC/^A
 *    orientation parameters are honoured.
 *  - ^PO accepts only N and I (the only values Zebra defines).
 *  - ^GF ASCII hex graphics, including Zebra's run-length compression, are
 *    decoded here. Z64/B64 graphics produce a warning.
 *  - Unknown commands produce one warning each instead of being skipped silently.
 *  - Multiple ^XA…^XZ blocks are parsed as separate labels (parseZplLabels).
 *  - ^BQ symbols sit lower than their ^FO by the ^BY height, as Zebra/Labelary
 *    draw them (zebraQrTopOffsetDots).
 * Not ported: EPL, element_meta, autoGrow, placeholder substitution (Phoenix
 * renders the final ZPL on the server).
 */

import {
  type BarcodeElement,
  type BarcodeType,
  type FieldBlock,
  type FontSpec,
  type LabelDoc,
  type LabelElement,
  type LinearOptions,
  type MatrixOptions,
  type QrOptions,
  type Rotation,
  createLabelDoc,
} from './document-model';
import { ZPL_CMD_TO_BARCODE, checkBarcodeData, zebraQrTopOffsetDots } from './barcode-types';
import { DEFAULT_FONT, isKnownFont, lineBoxHeightDots } from './font-mapper';

export interface ZplToken {
  prefix: '^' | '~';
  /** Command name, upper-cased: two characters, or 'A' + font for ^A. */
  cmd: string;
  /** Raw text between the command name and the next ^ or ~. */
  raw: string;
}

export interface ParseResult {
  doc: LabelDoc;
  warnings: string[];
}

/** Commands that are parsed but have no visual effect on a single label preview. */
const NON_VISUAL = new Set(['XA', 'XZ', 'FX', 'CI', 'PQ', 'PR', 'MD', 'MN', 'MT', 'MM', 'SZ', 'JU', 'JZ', 'FN']);

const SUPPORTED_COMMANDS: ReadonlySet<string> = new Set([
  ...NON_VISUAL,
  'PW', 'LL', 'LH', 'LR', 'PO', 'CF', 'FW', 'BY', 'FO', 'FT', 'FD', 'FS', 'FH', 'FR', 'FB',
  'GB', 'GC', 'GD', 'GF',
  ...Object.keys(ZPL_CMD_TO_BARCODE),
]);

/** True when the parser understands a ^ command (two-letter name; ^A + font counts as A). */
export function isSupportedCommand(cmd: string): boolean {
  const c = cmd.toUpperCase();
  return c.startsWith('A') ? isKnownFont(c.slice(1)) || c === 'A@' : SUPPORTED_COMMANDS.has(c);
}

const ORIENTATION: Readonly<Record<string, Rotation>> = { N: 0, R: 90, I: 180, B: 270 };

// ---------------------------------------------------------------------------
// Tokenizer
// ---------------------------------------------------------------------------

/**
 * Split ZPL into commands. Each token's raw text runs to the next ^ or ~,
 * which is how the printer delimits both parameters and ^FD data.
 */
export function tokenizeZpl(zpl: string): ZplToken[] {
  const src = zpl.replace(/[\r\n]/g, '');
  const tokens: ZplToken[] = [];
  let i = 0;
  while (i < src.length) {
    const ch = src[i];
    if (ch !== '^' && ch !== '~') {
      i++;
      continue;
    }
    const prefix = ch;
    // Command names are two characters; for ^A the second one is the font (^A0, ^AB, ^A@).
    const cmd = src.slice(i + 1, i + 3).toUpperCase();
    const start = Math.min(i + 3, src.length);
    let end = start;
    while (end < src.length && src[end] !== '^' && src[end] !== '~') end++;
    tokens.push({ prefix, cmd, raw: src.slice(start, end) });
    i = end;
  }
  return tokens;
}

function splitParams(raw: string): string[] {
  return raw === '' ? [] : raw.split(',').map((p) => p.trim());
}

function int(v: string | undefined): number | null {
  if (v === undefined || v === '') return null;
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? null : n;
}

function num(v: string | undefined): number | null {
  if (v === undefined || v === '') return null;
  const n = parseFloat(v);
  return Number.isNaN(n) ? null : n;
}

function color(v: string | undefined): 'B' | 'W' {
  return (v ?? 'B').toUpperCase() === 'W' ? 'W' : 'B';
}

function yesNo(v: string | undefined, dflt: boolean): boolean {
  if (v === undefined || v === '') return dflt;
  return v.toUpperCase() === 'Y';
}

// ---------------------------------------------------------------------------
// ^FH and ^GF decoding
// ---------------------------------------------------------------------------

/** Decode ^FH hex escapes (indicator + two hex digits). Under ^CI28 the bytes are UTF-8. */
export function decodeFieldHex(data: string, indicator: string, utf8: boolean): string {
  const bytes: number[] = [];
  let out = '';
  const flush = () => {
    if (!bytes.length) return;
    out += utf8 ? new TextDecoder('utf-8').decode(new Uint8Array(bytes)) : String.fromCharCode(...bytes);
    bytes.length = 0;
  };
  for (let i = 0; i < data.length; i++) {
    const hex = data.slice(i + 1, i + 3);
    if (data[i] === indicator && /^[0-9A-Fa-f]{2}$/.test(hex)) {
      bytes.push(parseInt(hex, 16));
      i += 2;
    } else {
      flush();
      out += data[i];
    }
  }
  flush();
  return out;
}

const REPEAT_VALUES: Readonly<Record<string, number>> = (() => {
  const map: Record<string, number> = {};
  'GHIJKLMNOPQRSTUVWXY'.split('').forEach((c, i) => (map[c] = i + 1));
  'ghijklmnopqrstuvwxyz'.split('').forEach((c, i) => (map[c] = (i + 1) * 20));
  return map;
})();

/**
 * Decode ^GF ASCII hex data, including Zebra's compression: G–Y / g–z repeat
 * counts, "," = fill the rest of the row with 0, "!" = fill with 1 (F),
 * ":" = repeat the previous row.
 */
export function decodeGraphicHex(data: string, totalBytes: number, rowBytes: number): Uint8Array {
  const out = new Uint8Array(totalBytes);
  const rowChars = rowBytes * 2;
  let row: string[] = [];
  let prevRow: string[] = new Array(rowChars).fill('0');
  let byteOffset = 0;
  let repeat = 0;

  const commitRow = () => {
    while (row.length < rowChars) row.push('0');
    for (let c = 0; c < rowChars && byteOffset < totalBytes; c += 2) {
      out[byteOffset++] = parseInt(row[c] + row[c + 1], 16);
    }
    prevRow = row;
    row = [];
  };

  for (const ch of data.replace(/\s/g, '')) {
    if (byteOffset >= totalBytes) break;
    if (ch in REPEAT_VALUES) {
      repeat += REPEAT_VALUES[ch];
      continue;
    }
    if (ch === ',' || ch === '!') {
      const fill = ch === ',' ? '0' : 'F';
      while (row.length < rowChars) row.push(fill);
      commitRow();
      repeat = 0;
      continue;
    }
    if (ch === ':') {
      row = prevRow.slice();
      commitRow();
      repeat = 0;
      continue;
    }
    if (/[0-9A-Fa-f]/.test(ch)) {
      const n = repeat || 1;
      for (let k = 0; k < n; k++) row.push(ch.toUpperCase());
      repeat = 0;
      while (row.length >= rowChars) {
        const rest = row.slice(rowChars);
        row = row.slice(0, rowChars);
        commitRow();
        row = rest;
      }
    }
  }
  if (row.length && byteOffset < totalBytes) commitRow();
  return out;
}

// ---------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------

interface BarcodeCommand {
  type: BarcodeType;
  params: string[];
}

interface FieldState {
  x: number;
  y: number;
  /** ^FT: origin is the baseline / bottom edge rather than the top. */
  typeset: boolean;
  font: FontSpec | null;
  rotate: Rotation | null;
  block: FieldBlock | null;
  reverse: boolean;
  hexIndicator: string | null;
  barcode: BarcodeCommand | null;
  data: string | null;
}

interface LabelState {
  font: FontSpec;
  orientation: Rotation;
  by: { moduleWidth: number; ratio: number; height: number };
  home: { x: number; y: number };
}

function newField(): FieldState {
  return {
    x: 0,
    y: 0,
    typeset: false,
    font: null,
    rotate: null,
    block: null,
    reverse: false,
    hexIndicator: null,
    barcode: null,
    data: null,
  };
}

class LabelParser {
  readonly doc: LabelDoc = createLabelDoc();
  readonly warnings: string[] = [];
  private readonly warned = new Set<string>();
  private state: LabelState = {
    font: { ...DEFAULT_FONT },
    orientation: 0,
    by: { moduleWidth: 2, ratio: 3, height: 10 },
    home: { x: 0, y: 0 },
  };
  private field: FieldState = newField();
  private fieldOpen = false;

  warn(message: string, key = message): void {
    if (this.warned.has(key)) return;
    this.warned.add(key);
    this.warnings.push(message);
  }

  private push(el: LabelElement): void {
    this.doc.elements.push(el);
  }

  apply(t: ZplToken): void {
    const p = splitParams(t.raw);
    if (t.prefix === '~') {
      this.warn(`Unsupported ZPL command ~${t.cmd} ignored`, `~${t.cmd}`);
      return;
    }
    if (t.cmd.startsWith('A') && t.cmd.length === 2) {
      this.fontCommand(t.cmd[1], p);
      return;
    }
    const barcode = ZPL_CMD_TO_BARCODE[t.cmd];
    if (barcode) {
      this.field.barcode = { type: barcode, params: p };
      return;
    }
    switch (t.cmd) {
      case 'PW':
        this.doc.meta.widthDots = int(p[0]);
        return;
      case 'LL':
        this.doc.meta.heightDots = int(p[0]);
        return;
      case 'LH':
        this.state.home = { x: int(p[0]) ?? 0, y: int(p[1]) ?? 0 };
        this.doc.meta.labelHome = { ...this.state.home };
        return;
      case 'LR':
        this.doc.meta.reverse = yesNo(p[0], false);
        return;
      case 'PO': {
        const v = (p[0] ?? 'N').toUpperCase();
        if (v === 'I') this.doc.meta.rotate = 180;
        else if (v === 'N') this.doc.meta.rotate = 0;
        else this.warn(`^PO${v}: only N and I are valid; used N`);
        return;
      }
      case 'CI':
        this.doc.meta.encoding = int(p[0]);
        return;
      case 'PQ':
        this.doc.meta.quantity = int(p[0]);
        return;
      case 'CF': {
        const name = (p[0] || this.state.font.name).toUpperCase();
        const height = int(p[1]) ?? this.state.font.height;
        // A new height without a width makes the width proportional again.
        const width = int(p[2]) ?? (int(p[1]) !== null ? 0 : this.state.font.width);
        this.checkFont(name);
        this.state.font = { name, height, width };
        return;
      }
      case 'FW':
        this.state.orientation = ORIENTATION[(p[0] ?? 'N').toUpperCase()] ?? 0;
        return;
      case 'BY':
        this.state.by = {
          moduleWidth: int(p[0]) ?? this.state.by.moduleWidth,
          ratio: num(p[1]) ?? this.state.by.ratio,
          height: int(p[2]) ?? this.state.by.height,
        };
        return;
      case 'FO':
      case 'FT':
        if (this.fieldOpen) {
          // Tolerated (migrations 019/022 do this): the new origin ends the open field.
          // Modifiers without data (e.g. ^BY, ^BC) carry over to the new origin.
          this.warn(`^FO${this.field.x},${this.field.y} has no closing ^FS; the next ^${t.cmd} ends it`, 'missing-FS');
          if (this.field.data !== null) this.emitField();
        }
        this.field.x = (int(p[0]) ?? 0) + this.state.home.x;
        this.field.y = (int(p[1]) ?? 0) + this.state.home.y;
        this.field.typeset = t.cmd === 'FT';
        this.fieldOpen = true;
        return;
      case 'FB':
        this.field.block = {
          maxWidth: int(p[0]) ?? 0,
          maxLines: int(p[1]) ?? 1,
          lineSpacing: int(p[2]) ?? 0,
          align: (['L', 'C', 'R', 'J'].includes((p[3] ?? 'L').toUpperCase()) ? (p[3] ?? 'L').toUpperCase() : 'L') as FieldBlock['align'],
          hangingIndent: int(p[4]) ?? 0,
        };
        if (this.field.block.align === 'J') this.warn('^FB justification J is previewed as left-aligned');
        return;
      case 'FR':
        this.field.reverse = true;
        return;
      case 'FH':
        this.field.hexIndicator = t.raw.length ? t.raw[0] : '_';
        return;
      case 'FD':
        this.field.data = t.raw;
        return;
      case 'FS':
        this.emitField();
        return;
      case 'GB':
      case 'GC':
      case 'GD':
      case 'GF':
        this.graphic(t.cmd, p, t.raw);
        return;
      default:
        if (NON_VISUAL.has(t.cmd)) return;
        this.warn(`Unsupported ZPL command ^${t.cmd} ignored`, `^${t.cmd}`);
    }
  }

  private checkFont(name: string): void {
    if (!isKnownFont(name)) this.warn(`Font ${name} is not a built-in font; previewed as font 0`, `font:${name}`);
  }

  private fontCommand(name: string, p: string[]): void {
    const fontName = name.toUpperCase();
    if (fontName === '@') {
      this.warn('^A@ (font by file name) is previewed as font 0');
    }
    const effectiveName = fontName === '@' ? '0' : fontName;
    this.checkFont(effectiveName);
    const orientation = (p[0] ?? '').toUpperCase();
    if (orientation in ORIENTATION) this.field.rotate = ORIENTATION[orientation];
    const height = int(p[1]) ?? this.state.font.height;
    const width = int(p[2]) ?? (int(p[1]) !== null ? 0 : this.state.font.width);
    this.field.font = { name: effectiveName, height, width };
  }

  private graphic(cmd: string, p: string[], raw: string): void {
    const { x, y, reverse } = this.field;
    if (cmd === 'GB') {
      const thickness = Math.max(1, int(p[2]) ?? 1);
      this.push({
        type: 'box',
        x,
        y,
        // Zebra raises width and height to at least the border thickness.
        w: Math.max(thickness, int(p[0]) ?? thickness),
        h: Math.max(thickness, int(p[1]) ?? thickness),
        thickness,
        color: color(p[3]),
        rounding: Math.min(8, Math.max(0, int(p[4]) ?? 0)),
        reverse,
      });
    } else if (cmd === 'GC') {
      const diameter = Math.max(3, int(p[0]) ?? 3);
      this.push({ type: 'circle', x, y, diameter, thickness: int(p[1]) ?? 1, color: color(p[2]), reverse });
    } else if (cmd === 'GD') {
      const thickness = int(p[2]) ?? 1;
      this.push({
        type: 'diagonal',
        x,
        y,
        w: int(p[0]) ?? thickness,
        h: int(p[1]) ?? thickness,
        thickness,
        color: color(p[3]),
        orientation: (p[4] ?? 'R').toUpperCase() === 'L' ? 'L' : 'R',
        reverse,
      });
    } else {
      const format = (p[0] ?? 'A').toUpperCase();
      const total = int(p[2]) ?? int(p[1]) ?? 0;
      const rowBytes = int(p[3]) ?? 0;
      // The data may itself contain commas (row fill), so take everything after the 4th comma.
      const data = raw.split(',').slice(4).join(',');
      if (format !== 'A' || data.startsWith(':Z64:') || data.startsWith(':B64:')) {
        this.warn(`^GF format ${format}${data.startsWith(':') ? ' ' + data.slice(0, 5) : ''} is not previewed`, 'GF-format');
      } else if (rowBytes > 0 && total > 0) {
        this.push({ type: 'graphic', x, y, rowBytes, bytes: decodeGraphicHex(data, total, rowBytes), reverse });
      }
    }
    this.fieldDone();
  }

  private fieldDone(): void {
    this.field = newField();
    this.fieldOpen = false;
  }

  private emitField(): void {
    const f = this.field;
    if (f.data !== null) {
      let data = f.data;
      if (f.hexIndicator !== null) data = decodeFieldHex(data, f.hexIndicator, this.doc.meta.encoding === 28);
      if (f.barcode) this.push(this.buildBarcode(f, f.barcode, data));
      else this.push(this.buildText(f, data));
    }
    this.fieldDone();
  }

  private buildText(f: FieldState, text: string): LabelElement {
    const font = f.font ?? { ...this.state.font };
    let y = f.y;
    if (f.typeset) {
      // ^FT places the baseline; approximate the top as one line above it.
      const lines = f.block ? Math.max(1, f.block.maxLines) : 1;
      y = f.y - lines * lineBoxHeightDots(font);
    }
    return {
      type: 'text',
      x: f.x,
      y,
      rotate: f.rotate ?? this.state.orientation,
      font,
      text,
      reverse: f.reverse,
      block: f.block,
    };
  }

  private buildBarcode(f: FieldState, cmd: BarcodeCommand, rawData: string): BarcodeElement {
    const p = cmd.params;
    const rotate = ORIENTATION[(p[0] ?? '').toUpperCase()] ?? this.state.orientation;
    const by = this.state.by;
    let data = rawData;
    let h = by.height;
    let showText = true;
    let options: QrOptions | LinearOptions | MatrixOptions;

    switch (cmd.type) {
      case 'qrcode': {
        // ^BQa,b,c,d,e — orientation, model, magnification, error correction, mask.
        let ec = ((p[3] ?? 'Q').toUpperCase() || 'Q') as QrOptions['errorCorrection'];
        const prefix = /^([LMQH])([AM]),/i.exec(data);
        if (prefix) {
          ec = prefix[1].toUpperCase() as QrOptions['errorCorrection'];
          data = data.slice(prefix[0].length);
          if (prefix[2].toUpperCase() === 'M') {
            // Manual input: a mode character, plus a 4-digit length for byte mode.
            const mode = data[0]?.toUpperCase();
            data = mode === 'B' ? data.slice(5) : data.slice(1);
          }
        } else {
          this.warn('^BQ data has no "<level><mode>," prefix (e.g. "QA,"); the printer will misread its first characters', 'qr-prefix');
        }
        if (!['L', 'M', 'Q', 'H'].includes(ec)) ec = 'Q';
        if ((p[0] ?? 'N').toUpperCase() !== 'N' && p[0]) this.warn('^BQ supports only normal orientation');
        options = { model: int(p[1]) ?? 2, magnification: int(p[2]) ?? 0, errorCorrection: ec };
        showText = false;
        const y = f.y + zebraQrTopOffsetDots(by.height);
        return { type: 'barcode', origin: { x: f.x, y: f.y }, x: f.x, y, h: 0, rotate: 0, barcodeType: 'qrcode', data, showText, options };
      }
      case 'datamatrix':
        options = { magnification: int(p[1]) ?? 0, columns: int(p[3]) ?? 0, rows: int(p[4]) ?? 0, eclevel: int(p[2]) ?? 200 };
        showText = false;
        break;
      case 'aztec':
        options = { magnification: int(p[1]) ?? 0, columns: 0, rows: 0 };
        showText = false;
        break;
      case 'pdf417':
      case 'micropdf417':
        h = int(p[1]) ?? by.height;
        options = { magnification: by.moduleWidth, columns: int(p[3]) ?? 0, rows: int(p[4]) ?? 0, eclevel: int(p[2]) ?? undefined };
        showText = false;
        break;
      case 'maxicode':
        options = { magnification: 0, columns: 0, rows: 0 };
        showText = false;
        break;
      case 'code39':
      case 'codabar':
      case 'msi':
        // ^B3o,e,h,f,g / ^BKo,e,h,f,g / ^BMo,e,h,f,g: check digit before height.
        h = int(p[2]) ?? by.height;
        showText = yesNo(p[3], true);
        options = { moduleWidth: by.moduleWidth, ratio: by.ratio, textAbove: yesNo(p[4], false) };
        break;
      case 'code128': {
        // ^BCo,h,f,g,e,m
        h = int(p[1]) ?? by.height;
        showText = yesNo(p[2], true);
        const mode = (p[5] ?? 'N').toUpperCase();
        options = {
          moduleWidth: by.moduleWidth,
          ratio: by.ratio,
          textAbove: yesNo(p[3], false),
          mode: (['N', 'U', 'A', 'D'].includes(mode) ? mode : 'N') as LinearOptions['mode'],
        };
        break;
      }
      default:
        // ^BEo,h,f,g / ^BUo,h,f,g,e / ^B2o,h,f,g,e …: height is the second parameter.
        h = int(p[1]) ?? by.height;
        showText = yesNo(p[2], true);
        options = { moduleWidth: by.moduleWidth, ratio: by.ratio, textAbove: yesNo(p[3], false) };
    }

    const problem = checkBarcodeData(cmd.type, data);
    if (problem) this.warn(`${cmd.type} barcode: ${problem}`, `data:${cmd.type}`);

    // ^FT puts a barcode's origin at its bottom-left corner.
    const y = f.typeset ? f.y - h : f.y;
    return { type: 'barcode', origin: { x: f.x, y: f.y }, x: f.x, y, h, rotate, barcodeType: cmd.type, data, showText, options };
  }

  finish(): void {
    if (this.fieldOpen) {
      this.warn(`^FO${this.field.x},${this.field.y} has no closing ^FS before ^XZ`, 'missing-FS-end');
      this.emitField();
    }
  }
}

function splitLabels(tokens: ZplToken[]): ZplToken[][] {
  const labels: ZplToken[][] = [];
  let cur: ZplToken[] | null = null;
  for (const t of tokens) {
    if (t.prefix === '^' && t.cmd === 'XA') {
      cur = [];
      labels.push(cur);
      continue;
    }
    if (t.prefix === '^' && t.cmd === 'XZ') {
      cur = null;
      continue;
    }
    if (cur) cur.push(t);
  }
  return labels;
}

function parseTokens(tokens: ZplToken[]): ParseResult {
  const parser = new LabelParser();
  for (const t of tokens) parser.apply(t);
  parser.finish();
  return { doc: parser.doc, warnings: parser.warnings };
}

/** Parse every ^XA…^XZ label in a ZPL stream. Text outside a label is ignored. */
export function parseZplLabels(zpl: string): ParseResult[] {
  return splitLabels(tokenizeZpl(zpl)).map(parseTokens);
}

/**
 * Parse the first label of a ZPL stream. Warns when the stream holds more than
 * one label or none at all; use parseZplLabels() to get them all.
 */
export function parseZpl(zpl: string): ParseResult {
  const labels = parseZplLabels(zpl);
  if (labels.length === 0) {
    const fallback = parseTokens(tokenizeZpl(zpl));
    fallback.warnings.unshift('No ^XA…^XZ label found; parsed the commands as one label');
    return fallback;
  }
  const first = labels[0];
  if (labels.length > 1) {
    first.warnings.push(`ZPL contains ${labels.length} labels; only the first is shown by parseZpl()`);
  }
  return first;
}
